import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable } from 'rxjs';
import { API_CONFIG } from './api.config';
import {map, debounceTime, share} from 'rxjs/operators';
import {environment} from "../../../environments/environment";

export interface ApiServiceConfig {
  url: string;
  errorResponseHandler: any;
  debounceRate: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private http: HttpClient,
    @Inject(API_CONFIG) @Optional() config: ApiServiceConfig
  ) {
    if (config) {
      if (config.hasOwnProperty('url'))
        this.setUrl(config.url);

      if (config.hasOwnProperty('errorResponseHandler'))
        this.config.errorResponseHandler = config.errorResponseHandler;

      if (config.hasOwnProperty('debounceRate'))
        this.config.debounceRate = config.debounceRate;
    }
  }

  // Default config settings 
  config: ApiServiceConfig = {
    url: environment.API,
    debounceRate : 450,
    errorResponseHandler: () => {}
  };

  setUrl(value: string) {
    // Make sure url ends with a '/'
    const lastChar = value.slice(-1);
    if (lastChar !== '/') value = `${value}/`;

    this.config.url = value;
  }

  setConfig(config: ApiServiceConfig) {
    Object.keys(config).forEach((key: any) => {
      if (this.config.hasOwnProperty(key))
        this.config[key] = config[key];
    });
  }

  setDebounceRate(value: number) {
    this.config.debounceRate = Math.floor(value);
  }

  // Global listener for all API calls which might return error responses
  listenForSpecialResponse(response: Observable<any>) {
    response.subscribe(()=>{}, this.config.errorResponseHandler, ()=>{});
  }

  // Api request methods
  // -------------------
  // Each method makes a call to the AuthHttp service, which wraps Http and adds extra functionality useful
  // for JWT protected calls. They also subscribe to the Observable and redirect to the unauth page on a 401 response
  get(resource: string, options: any = {}, url = this.config.url): Observable<any> {
    const response = this.http
      .get(`${url}${resource}`, options)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }

  getBlob(resource: string, url = this.config.url): Observable<any> {
    const response = this.http.get(`${url}${resource}`, { 
      responseType: 'blob',
      observe: 'response'
    }).pipe(share());
    this.listenForSpecialResponse(response);
    return response;
  }

  post(resource: string, data = {}, options: any = {}, url = this.config.url): Observable<any> {
    const response = this.http
      .post(`${url}${resource}`, data, options)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }

  put(resource: string, data = {}, options: any = {}, url = this.config.url): Observable<any> {
    const response = this.http
      .put(`${url}${resource}`, data, options)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }

  delete(resource: string, options: any = {}, url = this.config.url): Observable<any> {
    const response = this.http
      .delete(`${url}${resource}`, options)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }

  patch(resource: string, data = {}, options: any = {}, url = this.config.url): Observable<any> {
    const response = this.http
      .patch(`${url}${resource}`, data, options)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }

  head(resource: string, url = this.config.url): Observable<any> {
    const response = this.http
      .head(`${url}${resource}`)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }

  options(resource: string, url = this.config.url): Observable<any> {
    const response = this.http
      .options(`${url}${resource}`)
      .pipe(
        map(res => res),
        debounceTime(this.config.debounceRate),
        share()
      );

    this.listenForSpecialResponse(response);

    return response;
  }
}
